import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import {
  Page,
  PageFormFields,
  PagesBulkPublishAction,
  PagesBulkPublishScope,
  PageSearchAndReplaceAutocompleteQuery,
  PageSearchAndReplaceFormFields,
  PagesQueryKeys,
  PageUsageForType,
  ReplacementVersion,
  SearchAndReplaceAutocompleteResponse,
  SearchAndReplaceRollbackResponse,
} from '@/modules/Pages';

type UseGetAllPagesListByPagesQueryResult = WithPagination<
  Pick<Page, 'id' | 'title' | 'usageFor' | 'publishedAt' | 'createdAt' | 'updatedAt' | 'isPublished'> & {
    structureNode: Pick<Page['structureNode'], 'id' | 'isHome' | 'extension' | 'path' | 'slug'> & {
      site: Pick<Page['structureNode']['site'], 'id' | 'name'>;
      pages: Pick<Page['structureNode']['pages'][0], 'id'>[];
    };
  }
>;

export const useGetAllPagesListByPagesQuery = (siteId: string, paginationOptions: AppPaginationOptions) =>
  buildQuery<UseGetAllPagesListByPagesQueryResult, QueryError>([PagesQueryKeys.GetAll, siteId, paginationOptions], async () => {
    const { data } = await apiExecutor.get<UseGetAllPagesListByPagesQueryResult>(ApiRoutes.Pages.replace(':siteId', siteId), {
      params: paginationOptions,
    });
    return data;
  });

export type PagesBulkPublishListParams = AppPaginationOptions & {
  isPublished: boolean;
  usageForVariants: PageUsageForType[];
};

type UseGetPagesBulkPublishListQueryResult = WithPagination<
  Pick<Page, 'id' | 'title' | 'publishedAt' | 'isPublished'> & {
    structureNode: Pick<Page['structureNode'], 'path'>;
  }
>;

export const useGetPagesBulkPublishListQuery = (siteId: string, params: PagesBulkPublishListParams) =>
  buildQuery<UseGetPagesBulkPublishListQueryResult, QueryError>(
    [PagesQueryKeys.BulkPublishList, siteId, params],
    async () => {
      const { data } = await apiExecutor.get<UseGetPagesBulkPublishListQueryResult>(
        ApiRoutes.PagesPublication.replace(':siteId', siteId),
        { params: { ...params, usageForVariants: JSON.stringify(params.usageForVariants) } },
      );
      return data;
    },
  );

type UseGetOneByIdDossierPagesQueryResult = Pick<
  Page,
  'id' | 'title' | 'usageFor' | 'publishedAt' | 'createdAt' | 'updatedAt' | 'previewLink' | 'isPublished'
> & {
  structureNode: Pick<Page['structureNode'], 'id' | 'isHome' | 'extension' | 'path' | 'slug'> & {
    site: Pick<Page['structureNode']['site'], 'id' | 'name'>;
    pages: Pick<Page['structureNode']['pages'][0], 'id' | 'title' | 'usageFor'>[];
  };
};

export const useGetOneByIdDossierPagesQuery = (id: string, siteId: string) =>
  buildQuery<UseGetOneByIdDossierPagesQueryResult, QueryError>([PagesQueryKeys.GetOneById, siteId, id], async () => {
    const { data } = await apiExecutor.get<UseGetOneByIdDossierPagesQueryResult>(
      ApiRoutes.PagesByIdDossier.replace(':siteId', siteId).replace(':pageId', id),
    );
    return data;
  });

type UseGetOneByIdPagesQueryResult = Pick<
  Page,
  'id' | 'title' | 'usageFor' | 'publishedAt' | 'createdAt' | 'updatedAt' | 'previewLink' | 'body'
> & {
  layout: Page['layout'];
  structureNode: (Pick<Page['structureNode'], 'id' | 'isHome' | 'extension' | 'path' | 'slug'> & {
    site: Pick<Page['structureNode']['site'], 'id' | 'name'> & { siteHaveSearchConsole: boolean };
    pages: Pick<Page['structureNode']['pages'][0], 'id' | 'title' | 'usageFor'>[];
  });
  pageCriticalCss: Page['pageCriticalCss'];
};

export const useGetOneByIdPagesQuery = (id: string, siteId: string) =>
  buildQuery<UseGetOneByIdPagesQueryResult, QueryError>([PagesQueryKeys.GetOneByIdDossier, siteId, id], async () => {
    const { data } = await apiExecutor.get<UseGetOneByIdPagesQueryResult>(
      ApiRoutes.PagesById.replace(':siteId', siteId).replace(':pageId', id),
    );
    return data;
  });

export const useCreatePagesMutation = (siteId: string) =>
  buildMutation<Page, QueryError, PageFormFields>([PagesQueryKeys.Create, siteId], async (postData) => {
    const { data } = await apiExecutor.post<Page>(ApiRoutes.Pages.replace(':siteId', siteId), {
      ...postData,
    });
    return data;
  });

export const useEditPagesMutation = (id: string, siteId: string) =>
  buildMutation<Page, QueryError, PageFormFields>([PagesQueryKeys.Edit, siteId, id], async (postData) => {
    const { data } = await apiExecutor.patch<Page>(ApiRoutes.PagesById.replace(':siteId', siteId).replace(':pageId', id), {
      ...postData,
      id,
    });
    return data;
  });

export const useDeletePagesMutation = (id: string, siteId: string) =>
  buildMutation<void, QueryError, void>([PagesQueryKeys.Delete, siteId, id], async () => {
    await apiExecutor.delete<Page>(ApiRoutes.PagesById.replace(':siteId', siteId).replace(':pageId', id));
  });

export const useBreadcrumbPagesQuery = (id: string, siteId: string) =>
  buildQuery<string, QueryError>([PagesQueryKeys.Breadcrumb, siteId, id], async () => {
    const { data } = await apiExecutor.get<string>(ApiRoutes.PagesBreadcrumb.replace(':siteId', siteId).replace(':pageId', id));
    return data;
  });

export const useSearchAndReplaceTextPagesMutation = (siteId: string) =>
  buildMutation<void, QueryError, PageSearchAndReplaceFormFields>([PagesQueryKeys.SearchAndReplace, siteId], async (postData) => {
    await apiExecutor.patch<Page>(ApiRoutes.PageSearchAndReplace.replace(':siteId', siteId), {
      ...postData,
    });
  });

export const useAutocompleteSearchAndReplaceQuery = (
  params: { query: PageSearchAndReplaceAutocompleteQuery },
  siteId: string,
) =>
  buildQuery<SearchAndReplaceAutocompleteResponse, QueryError>(
    [PagesQueryKeys.AutocompleteSearchAndReplace, siteId, params],
    async ({ signal }) => {
      const { data } = await apiExecutor.get<SearchAndReplaceAutocompleteResponse>(
        ApiRoutes.PageSearchAndReplaceAutocomplete.replace(':siteId', siteId),
        {
          params: {
            q: params.query.searchFor,
            matchCase: params.query.matchCase ?? false,
            wholeWord: params.query.wholeWord ?? false,
            useRegex: params.query.useRegex ?? false,
          },
          signal,
        },
      );
      return data;
    },
  );

export const useSearchAndReplaceHistoryQuery = (
  siteId: string,
) =>
  buildQuery<ReplacementVersion[], QueryError>(
    [PagesQueryKeys.SearchAndReplaceHistory, siteId],
    async () => {
      const { data } = await apiExecutor.get<ReplacementVersion[]>(
        ApiRoutes.PageSearchAndReplaceHistory.replace(':siteId', siteId),
      );
      return data;
    },
  );

export const useSearchAndReplaceHistoryRollbackMutation = (siteId: string) =>
  buildMutation<SearchAndReplaceRollbackResponse, QueryError, { version: string }>(
    [PagesQueryKeys.SearchAndReplaceHistoryRollback, siteId],
    async ({ version }) => {
      const { data } = await apiExecutor.post<SearchAndReplaceRollbackResponse>(
        ApiRoutes.PageSearchAndReplaceHistoryRollback.replace(':siteId', siteId).replace(':version', version),
      );
      return data;
    },
  );

type UseBulkPublishPagesMutationResult = {
  scope: PagesBulkPublishScope;
  publishedAt?: string;
  pageIds?: string[];
  usageForVariants: PageUsageForType[];
};

export const useBulkPublishPagesMutation = (siteId: string, action: PagesBulkPublishAction) =>
  buildMutation<{ updatedCount: number }, QueryError, UseBulkPublishPagesMutationResult>(
    [PagesQueryKeys.BulkPublish, siteId, action],
    async (postData) => {
      const route = action === 'publish' ? ApiRoutes.PagesPublicationPublish : ApiRoutes.PagesPublicationUnpublish;
      const { data } = await apiExecutor.patch<{ updatedCount: number }>(route.replace(':siteId', siteId), {
        ...postData,
      });
      return data;
    },
  );
